import { Component, OnInit, OnDestroy } from "@angular/core";
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../common/utils/froala.utils';
import { FormGroup, FormBuilder, Validators, AbstractControl } from "@angular/forms";
import { Dialog, DialogButton, DialogControl, DialogControlType, PropertyDependency } from "../../models/dialog.model";
import { DynamicDialogService } from "../../services/dynamic-dialog.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { Option } from "../../../common/models/flow.dto";


@Component({
    selector: "dynamic-dialog",
    templateUrl: "./dynamic-dialog.component.html",
    styleUrls: ["./dynamic-dialog.component.scss"],
    standalone: false
})
export class DynamicDialogComponent implements OnInit, OnDestroy {

    get controls(): DialogControl[] {
        if (this.dialog == null || this.dialog.controls == null) return [];
        return this.dialog.controls;
    }
    dialog = new Dialog("", [])

    private editorKey: string;
    private _froalaOptions: any = null;

    excelStyles: any = this.formatter.excelStyles;

    form: FormGroup = new FormGroup({});

    get froalaOptions(): any {
        if (!this._froalaOptions) {
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 300,
                heightMin: 300,
                key: this.editorKey,
                saveInterval: 0,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough'],
                typingTimer: 150,
                autofocus: true
            });
        }
        return this._froalaOptions;
    }


    types = DialogControlType;    

    subscription: Subscription;

    get message(): string {
        return this.dialog.message;
    }

    get showDialog(): boolean {
        return this.dialog.visibility;
    }

    constructor(public readonly dialogService: DynamicDialogService, private builder: FormBuilder, private formatter: GridFormatterService) {
        this.form = this.builder.group({})
        let conf = window["PdfConfig"] as any;
        this.editorKey = (conf != null) ? conf.editorLicenseKey : "";
    }



    ngOnInit() {
        this.subscription = this.dialogService.onDialogChange().pipe(
            tap((dialog) => {
                this.form.reset();
                if (dialog.controls != null && dialog.controls.length > 0) {
                   this.form = this.buildForm(dialog.controls);
                }
                this.dialog = dialog;
            })).subscribe();
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    buildForm(controls: DialogControl[]): FormGroup {
        let obj: any = {};
        controls.forEach(f => {
            let validators: Validators[] = f.validators;
            obj[f.id] = [{ value: f.value, disabled: false }, validators];
        });
        let tempForm = this.builder.group(obj);
        controls.forEach(f => {
            let disabled = this.controlIsDisabled(f.id, tempForm);
            let ctrl = tempForm.get(f.id);
            if (ctrl != null) {
                if (disabled) ctrl.disable();
                else ctrl.enable();
            }
        })
        return tempForm;
    }

    checkboxChanged(event: Event): void {
        let canToggle: any = true;
        if (event.currentTarget != null && (event.currentTarget as any).id != null) {
            let ctrl = this.controls.find(c => c.id === (event.currentTarget as any).id);
            if (ctrl != null) {
                if (ctrl.controlDependencies != null && ctrl.controlDependencies.filter(c => c.property === PropertyDependency.CanToggleCheckbox).length > 0) {
                    let conditions = ctrl.controlDependencies.filter(c => c.property === PropertyDependency.CanToggleCheckbox);
                    conditions.forEach(c => {
                        let otherCtrl = this.form.get(c.controlName);
                        if (otherCtrl != null) {
                            let dependentValue = c.dependentValues.find(d => d.whenValueIs == otherCtrl.value);
                            if (dependentValue != null) canToggle = false;
                        }
                    })
                }
            }
        }
        if (canToggle == false) event.preventDefault();
    }

    //enable disable dependency checking currently only works if the control depends on a dropdown, as its wired into the change event tied to dropdowns.  will need to add to other events should it become needed later.
    controlIsDisabled(name: string, checkForm: FormGroup): boolean {
        let isDisabled: boolean = false;
        const ctrl = this.controls.find(c => c.id === name);
        if (ctrl != null) {
            if (ctrl.controlDependencies != null && ctrl.controlDependencies.length > 0) {
                let relevantDependencies = ctrl.controlDependencies.filter(c => c.property === PropertyDependency.Enabled);
                if (relevantDependencies != null && relevantDependencies.length > 0) {
                    relevantDependencies.forEach(r => {
                        if (isDisabled === false) {
                            let dependency = checkForm.get(r.controlName);
                            if (dependency != null) {
                                isDisabled = r.dependentValues.includes(dependency.value) == false;
                            }
                        }                       
                    })
                }
            }
        }
        return isDisabled;
    }

    controlIsVisible(name: string): boolean {
        let isVisible: boolean = true;
        const ctrl = this.controls.find(c => c.id === name);
        if (ctrl != null) {
            if (ctrl.controlDependencies != null && ctrl.controlDependencies.length > 0) {
                let relevantDependencies = ctrl.controlDependencies.filter(c => c.property === PropertyDependency.Visible);
                if (relevantDependencies != null && relevantDependencies.length > 0) {
                    relevantDependencies.forEach(r => {
                        if (isVisible === true) {
                            let dependency = this.form.get(r.controlName);
                            if (dependency != null) {
                                isVisible = r.dependentValues.includes(dependency.value);
                            }
                        }
                    })
                }
            }
        }
        return isVisible;
    }

    invokeButton(button: DialogButton): void {
        this.dialog.toggleDialog(false);
        this.dialogService.dialogConfirmed$.next(button.isConfirm);
        this.controls
            .filter(c => c.type === this.types.TextEditor)
            .forEach(c => {
                const ctrl = this.form.get(c.id);
                if (ctrl) ctrl.setValue(stripFroalaWatermark(ctrl.value), { emitEvent: false });
            });
        if (this.dialog.hasParam) {
            if (this.dialog.formName != null && this.dialog.formName != "") {
                button.click({ name: this.dialog.formName, form: this.form });
            } else {
                button.click(this.form);
            }
        } else {
            button.click(null);
        }
    }

    onDateChanged(event: any) {
        let ctrl = this.form.get(event.name);
        if (ctrl != null) {
            ctrl.setValue(event.date);
        }
    }

    //will need other events, conditionally set them if/when other change events besides editing another control's options becomes necessary
    updateControlOptions(event: any): void {
        if (event != null && event.target != null) {
            let sourceControlId = event.target.id;
            if (sourceControlId != null) {
                let sourceFormControl = this.form.get(sourceControlId);
                let sourceControl = this.controls.find(c => c.id === sourceControlId);
                if (sourceControl != null && sourceFormControl != null) {
                    if (sourceControl.controlTargets != null && sourceControl.controlTargets.length > 0) {
                        sourceControl.controlTargets.forEach(t => {
                            let targetControlId = t.controlName;
                            let targetControl = this.controls.find(c => c.id === targetControlId);
                            let targetFormControl = this.form.get(targetControlId);
                            if (targetControl != null && targetFormControl != null) {
                                let valueReset: any;
                                switch (targetControl.type) {
                                    case DialogControlType.Dropdown: {
                                        valueReset = "";
                                        let optionSet = t.optionSets.find(s => s.value === sourceFormControl.value);
                                        if (optionSet != null) {
                                            targetControl.options = optionSet.set;
                                            if (optionSet.set.find(o => o.id === " ") != null) valueReset = " ";
                                        } else {
                                            targetControl.options = [{ id: " ", text: "" } as Option];
                                            valueReset = " ";
                                        }
                                        break;
                                    }
                                    case DialogControlType.Checkbox: {
                                        valueReset = false;
                                        let optionSet = t.optionSets.find(s => s.value === sourceFormControl.value);
                                        if (optionSet != null) {
                                            valueReset = optionSet.booleanValue;                                            
                                        }
                                        break;
                                    }
                                    default: {
                                        console.error(`target control type ${targetControl.type} not supported by updateControlOptions`);
                                    }
                                }
                                if (valueReset != null) targetFormControl.setValue(valueReset);
                            }
                        });
                    }
                    let dependentControls = this.controls.filter(c => c.controlDependencies.filter(c => c.property == PropertyDependency.Enabled && c.controlName == sourceControl.id));
                    if (dependentControls != null && dependentControls.length > 0) {
                        dependentControls.forEach(ctrl => {
                            let dependencies = ctrl.controlDependencies.filter(c => c.property == PropertyDependency.Enabled && c.controlName == sourceControl.id);
                            dependencies.forEach(dep => {
                                let dependency = this.form.get(dep.controlName);
                                let dependentControl = this.form.get(ctrl.id);
                                if (dependency != null && dependentControl != null) {
                                    let isDisabled = dep.dependentValues.includes(dependency.value) == false;
                                    if (isDisabled) dependentControl.disable();
                                    else dependentControl.enable();
                                }
                            })                          
                        });
                    }
                   
                }
            }
        }
    }
}
